import React, { useState, useRef, useEffect } from 'react';
import { Button, Grid2 as Grid, Menu, MenuItem, TextField } from '@mui/material';
import { withStyles } from '@mui/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { config } from './Expense/ExpenseReviewConfig';
import { COLORS } from '../../utils/Application_Constants';

const styles = () => ({
    searchKeyButton: {
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        height: '37px',
        minWidth: '130px',
        justifyContent: 'space-between',
    },
    formControl: {
        marginBottom: '0',
        minWidth: '215px',
        '& .MuiOutlinedInput-notchedOutline': {
            borderRadius: '6px',
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
        },
        borderRadius: '6px',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
    },
    input: {
        border: `1px solid ${COLORS.LT_MIDNIGHT5}`,
        borderRadius: '6px',
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        padding: '8px 16px 7px !important',
        height: 'auto',
        fontSize: '14px',
        lineHeight: '20px',
    },
    searchHelperText: { position: 'absolute', bottom: '-24px', marginLeft: 0 },
});

const MAX_SEARCH_LENGTH = 200;

const SearchInputWithDropdown = ({
    classes,
    isLoading,
    options = config.searchKeys,
    placeholder,
    initialSearch,
    initialSearchKey,
    onSubmit,
    ...props
}) => {
    const inputRef = useRef(null);
    const [searchKey, setSearchKey] = useState(() => {
        // Ensures initial search key is valid if provided, otherwise defaults to first option
        const searchKeyData = options?.find(({ value }) => value === initialSearchKey);
        if (initialSearchKey && searchKeyData) return initialSearchKey;
        return options?.[0]?.value;
    });
    const [search, setSearch] = useState(initialSearch ?? '');

    const searchHelperText = options?.find(({ value }) => value === searchKey)?.helperText;

    useEffect(() => {
        setSearch(initialSearch ?? '');
    }, [initialSearch]);

    useEffect(() => {
        // Focus on input when loading is finished only if search is not empty
        if (!isLoading && inputRef.current && search) inputRef.current.focus();
    }, [isLoading]);

    const submitSearch = () => {
        if (!searchKey) return;
        if (search.length > MAX_SEARCH_LENGTH) return;
        onSubmit({ search, searchKey });
    };

    const [changeSearchKeyButtonRef, setChangeSearchKeyButtonRef] = useState(null);
    const handleChangeSearchKey = (event) => setChangeSearchKeyButtonRef(event.currentTarget);
    const handleClose = () => setChangeSearchKeyButtonRef(null);

    // Changing the search column does not fire a search. The user has to press Enter
    // (or clear the input) for any API call to happen.
    const onSearchKeyChange = (newSearchKey) => {
        setSearchKey(newSearchKey);
        handleClose();
    };

    const handleSearchInputChange = (e) => {
        const newValue = e.target.value;
        setSearch(newValue);

        if (newValue === '') {
            // Clear button was clicked or input was cleared. Fire an empty submit so
            // any active search results reset.
            onSubmit({ search: '', searchKey });
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        submitSearch();
    };

    const open = Boolean(changeSearchKeyButtonRef);
    const searchKeyName = options?.find(({ value }) => value === searchKey)?.name;

    let error = '';
    if (search && search.length > MAX_SEARCH_LENGTH) {
        error = `Must be less than ${MAX_SEARCH_LENGTH} characters`;
    }
    return (
        <Grid container {...props}>
            <Grid>
                <Button
                    disabled={isLoading}
                    onClick={handleChangeSearchKey}
                    variant="contained"
                    color="primary"
                    endIcon={<ExpandMoreIcon fontSize="small" />}
                    className={classes.searchKeyButton}
                    aria-controls={open ? 'search-key-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                >
                    {searchKeyName}
                </Button>
                <Menu
                    id="search-key-menu"
                    anchorEl={changeSearchKeyButtonRef}
                    keepMounted
                    open={open}
                    onClose={handleClose}
                >
                    {options?.map((searchType) => (
                        <MenuItem
                            key={searchType.value}
                            selected={searchKey === searchType.value}
                            onClick={() => onSearchKeyChange(searchType.value)}
                        >
                            {searchType.name}
                        </MenuItem>
                    ))}
                </Menu>
            </Grid>
            <Grid>
                <form onSubmit={handleSubmit}>
                    <TextField
                        inputRef={inputRef}
                        disabled={isLoading}
                        type="search"
                        className={classes.formControl}
                        variant="outlined"
                        name="search"
                        id="search"
                        value={search}
                        onChange={handleSearchInputChange}
                        data-testid="search-input"
                        slotProps={{
                            input: { classes: { input: classes.input } },
                            formHelperText: { className: classes.searchHelperText },
                        }}
                        error={!!error}
                        helperText={error || searchHelperText}
                        placeholder={placeholder ?? 'Search'}
                        title={placeholder ?? 'Search'}
                    />
                </form>
            </Grid>
        </Grid>
    );
};

export default withStyles(styles)(SearchInputWithDropdown);
